import { ReaderPresentation, ReaderPresentationComponent } from '../shared/reader-presentation.component';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { PageDataService } from '../core/page-data.service';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { ResponsiveImageComponent } from '../shared/responsive-image.component';
import { LiveCallService, CallAvailability } from '../core/live-call.service';
import { ApiClientService } from '../core/api-client.service';
import { firstValueFrom } from 'rxjs';

type MediaSizes = Record<string, unknown> | unknown[] | null | undefined;

interface SellerPageData {
  success?: boolean;
  result?: boolean;
  data?: {
    seller?: SellerProfile;
    live?: SellerLive | null;
    listings?: SellerListing[];
    readings?: SellerListing[];
    feeds?: SellerFeed[];
    pastReadings?: Array<Record<string, unknown>>;
    pastReadingsPagination?: { hasMore?: boolean; page?: number };
    showPastReadings?: boolean;
    reviews?: SellerReview[];
    blogs?: SellerBlog[];
    fortuneOffers?: SellerFortuneOffer[];
  };
}

interface SellerFortuneOffer {
  offerID: number;
  categoryID: number;
  title: string;
  slug: string;
  description: string;
  creditPrice: number;
  imageUrl: string | null;
  imageSizes?: MediaSizes;
}

interface SellerProfile {
  presentation?:ReaderPresentation|null;
  id: number;
  sellerID: number;
  shopName: string | null;
  userName: string | null;
  sellerName: string;
  title: string | null;
  description: string | null;
  profilePicture: string | null;
  profilePictureSizes: MediaSizes;
  liveStatus: string | null;
  rating: number;
  reviewCount: number;
  orderCount: number;
  stats?: Record<string, number | string | null>;
}

interface SellerLive {
  liveID: number;
  channelName: string | null;
  title: string | null;
  coverImageUrl: string | null;
  viewerCount: number;
  startedAt: string | null;
  status: string | null;
  isLive: boolean;
}

interface SellerListing {
  id: number;
  title: string;
  slug: string;
  categoryTitle?: string | null;
  categoryName?: string | null;
  minPrice?: number | null;
  price?: number | null;
  credit?: number | null;
  minCredit?: number | null;
  currency?: string | null;
  images?: Array<{ url?: string | null; sizes?: MediaSizes }>;
  sellerImage?: string | null;
  sellerImageSizes?: MediaSizes;
  rating?: number;
  reviewCount?: number;
  sellerOrderCount?: number;
}

interface SellerFeed {
  feedID: number;
  caption: string | null;
  mediaType: string;
  imageUrl: string | null;
  videoUrl: string | null;
  listingTitle: string;
  listingSlug: string;
  live?: SellerLive;
}

interface SellerReview {
  id: number | string;
  authorName: string | null;
  rating: number;
  comment: string;
  createdAt: string | null;
  categoryTitle?: string | null;
  categoryName?: string | null;
  listingTitle?: string | null;
  productTitle?: string | null;
  listingSlug?: string | null;
  productSlug?: string | null;
  title?: string | null;
  sellerReply?: string | null;
  callType?: string | null;
  audioQuality?: number | null;
  videoQuality?: number | null;
}

interface SellerBlog {
  id: number;
  title: string;
  slug: string;
  summary: string;
  imageUrl: string | null;
  imageSizes: MediaSizes;
  publishedAt: string | null;
}

@Component({
  selector: 'app-seller-page',
  imports: [DatePipe, DecimalPipe, ResponsiveImageComponent, RouterLink, ReaderPresentationComponent],
  template: `
    @if (seller(); as profile) {
      <section class="seller-page">
        <header class="seller-hero">
          <div class="portrait">
            @if (profile.profilePicture) {
              <app-responsive-image
                [url]="profile.profilePicture"
                [mediaSizes]="profile.profilePictureSizes"
                [alt]="profile.sellerName || profile.shopName || i18n.t('reader')"
                displaySizes="(max-width: 780px) 58vw, 360px"
              />
            } @else {
              <span>{{ initials(profile.sellerName || profile.shopName || '') }}</span>
            }
          </div>

          <div class="hero-copy">
            @if(!commerce()){<app-reader-presentation id="sample-reading" [content]="profile.presentation" [expanded]="true" [continuePath]="i18n.routePath('fortune')"/>}
            <p class="eyebrow">{{ profile.liveStatus || (commerce() ? copy().shop : i18n.t('reader')) }}</p>
            <h1>{{ commerce() ? (profile.shopName || profile.sellerName) : (profile.sellerName || profile.shopName) }}</h1>
            <p class="handle">@{{ profile.userName || profile.shopName }}</p>
            @if (profile.description || profile.title) {
              <p class="bio">{{ profile.description || profile.title }}</p>
            }
            <div class="stats">
              <span><b>{{ profile.rating | number:'1.1-2' }}</b>{{ i18n.t('rating') }}</span>
              <span><b>{{ profile.reviewCount }}</b>{{ i18n.t('reviews') }}</span>
              @if (!commerce()) {
                <a class="stat-link" [routerLink]="i18n.routePath('sellerHistory', { slug: routeSlug() })"><b>{{ profile.orderCount }}</b>{{ i18n.t('readings') }}</a>
              } @else {
                <span><b>{{ profile.orderCount }}</b>{{ commerce() ? copy().sales : i18n.t('readings') }}</span>
              }
              <span><b>{{ profile.stats?.['last7DaysOrders'] || 0 }}</b>{{ copy().last7Days }}</span>
            </div>
            @if (!commerce() && callAvailability(); as availability) {
              @if (!availability.isOwnSeller && (availability.settings.voiceEnabled || availability.settings.videoEnabled)) {
                <div class="call-actions">
                  @if (availability.settings.voiceEnabled) {
                    <button type="button" (click)="startCall('voice')" [disabled]="!availability.online || availability.busy">
                      {{ copy().voiceCall }} · {{ availability.settings.voiceCreditsPerMinute }} {{ copy().creditsPerMinute }}
                    </button>
                  }
                  @if (availability.settings.videoEnabled) {
                    <button type="button" (click)="startCall('video')" [disabled]="!availability.online || availability.busy">
                      {{ copy().videoCall }} · {{ availability.settings.videoCreditsPerMinute }} {{ copy().creditsPerMinute }}
                    </button>
                  }
                  <small>{{ availability.busy ? copy().busy : availability.online ? copy().online : copy().inactive }}</small>
                </div>
              }
            }
          </div>
        </header>

        @if (live(); as liveData) {
          <a class="live-panel" [routerLink]="i18n.routePath('live', { id: liveData.liveID })">
            <span class="pulse"></span>
            <div>
              <small>{{ copy().liveNow }}</small>
              <strong>{{ liveData.title || (commerce() ? copy().shopLive : copy().readerLive) }}</strong>
            </div>
            <b>{{ liveData.viewerCount || 0 }} {{ copy().viewers }}</b>
          </a>
        }

        <section class="section-block" [class.fortune-products-section]="!commerce()" [class.commerce-products-section]="commerce()" [class.category-offer-section]="usesFortuneCategorySales()">
          <div class="section-head">
            <div>
              <p class="eyebrow">{{ commerce() ? copy().products : copy().readingTypes }}</p>
              <h2>{{ commerce() ? copy().shopProducts : copy().offeredReadingTypes }}</h2>
            </div>
            <a [routerLink]="i18n.routePath('fortune')">{{ commerce() ? copy().backProducts : copy().backReadings }}</a>
          </div>
          @if (usesFortuneCategorySales()) {
            <div class="fortune-type-grid">
              @for (offer of fortuneOffers(); track offer.offerID) {
                <a class="fortune-type-card" [routerLink]="i18n.routePath('category', { slug: offer.slug })" [queryParams]="{sellerID: profile.sellerID}">
                  <span class="fortune-type-image">
                    @if (offer.imageUrl) {
                      <app-responsive-image [url]="offer.imageUrl" [mediaSizes]="offer.imageSizes" [alt]="categoryLabel(offer.title)" displaySizes="180px" [priority]="true" />
                    } @else { <b>{{ initials(offer.title) }}</b> }
                  </span>
                  <strong>{{ categoryLabel(offer.title) }}</strong>
                  <small>{{ offer.creditPrice | number:'1.0-2' }} {{priceUnit('CREDIT')}}</small>
                </a>
              } @empty { <p class="empty">{{copy().noReadingTypes}}</p> }
            </div>
          } @else {
            <div class="listing-grid">
              @for (listing of listings(); track listing.id) {
              <a class="listing-card" [routerLink]="i18n.routePath('listing', { slug: listing.slug })">
                @if (listing.images?.[0]?.url || (!commerce() && (profile.profilePicture || listing.sellerImage))) {
                  <app-responsive-image
                    [url]="!commerce() ? (profile.profilePicture || listing.sellerImage || null) : (listing.images?.[0]?.url || null)"
                    [mediaSizes]="!commerce() ? (profile.profilePictureSizes || listing.sellerImageSizes || listing.images?.[0]?.sizes) : listing.images?.[0]?.sizes"
                    [alt]="listing.title"
                    displaySizes="(max-width: 700px) 46vw, 260px"
                  />
                }
                <div>
                  @if (!commerce() && (listing.categoryTitle || listing.categoryName)) {
                    <small class="listing-category">{{ listing.categoryTitle || listing.categoryName }}</small>
                  }
                  <h3>{{ listing.title }}</h3>
                  <p>{{ (listing.rating || seller()?.rating || 0) | number:'1.1-2' }} {{ i18n.t('rating') }}</p>
                  <strong>{{ listingAmount(listing) | number:'1.0-2' }} {{ listingUnit(listing) }}</strong>
                </div>
              </a>
              } @empty { <p class="empty">{{ commerce() ? copy().noProducts : copy().noReadings }}</p> }
            </div>
          }
        </section>

        @if (!commerce()) {
          <section class="section-block fortune-reviews-section">
            <div class="section-head">
              <div>
                <p class="eyebrow">{{ copy().reviews }}</p>
                <h2>{{ copy().clientReviews }}</h2>
              </div>
            </div>
            <div class="review-list">
              @for (review of reviews(); track review.id) {
                @if (review.rating || review.comment) {
                  <article class="fortune-review-card">
                    <div class="review-author">
                      <span class="review-avatar">{{ initials(review.authorName || copy().client) }}</span>
                      <div>
                        <strong>{{ review.authorName || copy().client }}</strong>
                        <div class="review-stars" [attr.aria-label]="(review.rating || 0) + '/5'">
                          @for (star of ratingStars(review.rating); track $index) { <i [class.partial]="star.partial" [style.--rating-fill]="star.fill + '%'">★</i> }
                        </div>
                      </div>
                      @if (review.createdAt) { <time class="review-date" [attr.datetime]="review.createdAt">{{ review.createdAt | date:'dd.MM.yyyy HH:mm' }}</time> }
                    </div>
                    @if (usesFortuneCategorySales()) {
                      <small class="review-context">{{ review.categoryTitle || review.categoryName || copy().readingReview }}</small>
                    } @else if (review.categoryTitle || review.categoryName || review.listingTitle || review.productTitle || review.title) {
                      @if (review.listingSlug || review.productSlug) {
                        <a class="review-context review-product-link" [routerLink]="i18n.routePath('listing', { slug: review.listingSlug || review.productSlug || '' })">
                          {{ review.categoryTitle || review.categoryName || copy().readings }} · {{ review.listingTitle || review.productTitle || review.title }}
                        </a>
                      } @else {
                        <small class="review-context">{{ review.categoryTitle || review.categoryName || copy().readings }} · {{ review.listingTitle || review.productTitle || review.title }}</small>
                      }
                    }
                    @if (review.comment) { <p>{{ review.comment }}</p> }
                    @if (review.callType) { <small>{{ review.callType === 'video' ? copy().videoSession : copy().voiceSession }} · {{ copy().audio }} {{ review.audioQuality || '-' }}/5 @if (review.videoQuality) { · {{ copy().video }} {{ review.videoQuality }}/5 }</small> }
                    @if (review.sellerReply) { <footer><b>{{ copy().readerReply }}</b> {{ review.sellerReply }}</footer> }
                  </article>
                }
              } @empty { <p class="empty">{{ copy().noReviews }}</p> }
            </div>
          </section>
        }

        <section class="section-block feed-section">
          <div class="section-head">
            <div>
              <p class="eyebrow">{{ copy().feed }}</p>
              <h2>{{ commerce() ? copy().shopPosts : copy().readerPosts }}</h2>
            </div>
            <a [routerLink]="i18n.routePath('feeds')">{{ copy().allFeed }}</a>
          </div>
          <div class="feed-grid">
            @for (feed of feeds(); track feed.feedID) {
              <article>
                <div class="feed-media">
                  @if (feed.live?.isLive) {
                    <span class="live-chip">{{ copy().live }}</span>
                  }
                  @if (feed.mediaType === 'video' && feed.videoUrl) {
                    <video [src]="mediaUrl(feed.videoUrl)" muted playsinline preload="metadata"></video>
                  } @else if (feed.imageUrl || feed.live?.coverImageUrl) {
                    <app-responsive-image [url]="feed.imageUrl || feed.live?.coverImageUrl" [alt]="feed.caption || feed.listingTitle" displaySizes="(max-width: 700px) 46vw, 280px" />
                  } @else {
                    <span>{{ initials(profile.sellerName || '') }}</span>
                  }
                </div>
                <p>{{ feed.caption || feed.listingTitle || (commerce() ? copy().shopPost : copy().readerPost) }}</p>
              </article>
            } @empty {
              <p class="empty">{{ commerce() ? copy().noShopPosts : copy().noReaderPosts }}</p>
            }
          </div>
        </section>

        <section class="split-sections history-reviews-section">
          <div class="section-block legacy-reviews-section">
            <p class="eyebrow">{{ copy().reviews }}</p>
            <h2>{{ copy().clientReviews }}</h2>
            @for (review of reviews(); track review.id) {
              @if (review.rating || review.comment) {
              <blockquote>
                <strong>{{ review.authorName || copy().client }} · {{ review.rating | number:'1.1-2' }}</strong>
                @if (!commerce() && (review.categoryTitle || review.categoryName || review.listingTitle || review.productTitle || review.title)) {
                  <small class="review-context">
                    {{ review.categoryTitle || review.categoryName || copy().readings }} ·
                    {{ review.listingTitle || review.productTitle || review.title }}
                  </small>
                }
                <p>{{ review.comment }}</p>
                @if (review.callType) {
                  <small>{{ review.callType === 'video' ? copy().videoSession : copy().voiceSession }} · {{ copy().audio }} {{ review.audioQuality || '-' }}/5 @if (review.videoQuality) { · {{ copy().video }} {{ review.videoQuality }}/5 }</small>
                }
                @if (review.sellerReply) { <footer><b>{{ copy().readerReply }}</b> {{ review.sellerReply }}</footer> }
              </blockquote>
              }
            } @empty {
              <p class="empty">{{ copy().noReviews }}</p>
            }
          </div>
        </section>

        @if (blogs().length) {
          <section class="section-block blogs-section">
            <div class="section-head">
              <div>
                <p class="eyebrow">Blog</p>
                <h2>{{ commerce() ? copy().shopBlogs : copy().readerBlogs }}</h2>
              </div>
              <a [routerLink]="i18n.routePath('blog')">{{ copy().allBlogs }}</a>
            </div>
            <div class="blog-grid">
              @for (blog of blogs(); track blog.id) {
                <a [routerLink]="i18n.routePath('blogDetail', { slug: blog.slug })">
                  @if (blog.imageUrl) {
                    <app-responsive-image [url]="blog.imageUrl" [mediaSizes]="blog.imageSizes" [alt]="blog.title" displaySizes="(max-width: 700px) 92vw, 360px" />
                  }
                  <h3>{{ blog.title }}</h3>
                  <p>{{ blog.summary }}</p>
                </a>
              }
            </div>
          </section>
        }
      </section>
    } @else if (missing()) {
      <section class="page-card">
        <p class="eyebrow">{{ i18n.t('reader') }}</p>
        <h1>{{ commerce() ? copy().shopMissing : copy().readerMissing }}</h1>
        <a [routerLink]="i18n.routePath('sellers')">{{ commerce() ? copy().backProducts : copy().backReadings }}</a>
      </section>
    } @else {
      <section class="page-card">
        <p class="eyebrow">{{ i18n.t('reader') }}</p>
        <h1>{{ commerce() ? copy().shopLoading : copy().readerLoading }}</h1>
      </section>
    }
  `,
  styles: [`
    .seller-page {
      display: grid;
      gap: clamp(1.2rem, 3vw, 2rem);
      margin: 0 auto;
      max-width: 1180px;
    }

    .seller-hero,
    .section-block,
    .live-panel {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 34px;
      box-shadow: 0 24px 70px color-mix(in srgb, var(--shadow) 54%, transparent);
    }

    .seller-hero {
      align-items: center;
      display: grid;
      gap: clamp(1.2rem, 4vw, 3rem);
      grid-template-columns: minmax(220px, 360px) 1fr;
      padding: clamp(1rem, 4vw, 2.2rem);
    }

    .portrait {
      aspect-ratio: 1 / 1;
      border-radius: 32px;
      display: grid;
      overflow: hidden;
      place-items: center;
      background: radial-gradient(circle at 24% 18%, color-mix(in srgb, var(--accent) 44%, transparent), transparent 42%), var(--paper);
      color: var(--accent);
      font-size: clamp(2.2rem, 7vw, 4rem);
      font-weight: 950;
    }

    .hero-copy h1 {
      color: var(--ink);
      font-size: clamp(2.4rem, 7vw, 5.7rem);
      letter-spacing: -.08em;
      line-height: .9;
      margin: 0;
    }

    .handle,
    .bio,
    .empty {
      color: var(--muted);
      line-height: 1.65;
    }

    .bio {
      font-size: 1.08rem;
      max-width: 720px;
    }

    .stats {
      display: flex;
      flex-wrap: wrap;
      gap: .75rem;
      margin-top: 1rem;
    }

    .stats span {
      background: color-mix(in srgb, var(--accent) 10%, transparent);
      border-radius: 999px;
      color: var(--muted);
      padding: .72rem 1rem;
    }
    .stats .stat-link {
      color: var(--muted);
      padding: .72rem 0;
      text-decoration: underline;
      text-underline-offset: .18em;
    }

    .stats b {
      color: var(--ink);
      margin-right: .35rem;
    }

    .live-panel {
      align-items: center;
      color: inherit;
      display: flex;
      gap: 1rem;
      padding: 1.1rem 1.35rem;
      text-decoration: none;
    }

    .pulse {
      animation: livePulse 1.2s ease-in-out infinite;
      background: #ff3f4d;
      border-radius: 50%;
      box-shadow: 0 0 22px #ff3f4d;
      height: 16px;
      width: 16px;
    }

    .live-panel small,
    .eyebrow {
      color: var(--accent);
      font-size: .75rem;
      font-weight: 950;
      letter-spacing: .16em;
      text-transform: uppercase;
    }

    .live-panel strong,
    .live-panel b {
      display: block;
    }

    .live-panel b {
      margin-left: auto;
    }

    .section-block {
      padding: clamp(1rem, 3vw, 1.8rem);
    }

    .section-head {
      align-items: center;
      display: flex;
      gap: 1rem;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .section-head h2,
    .section-block h2 {
      color: var(--ink);
      margin: .2rem 0 0;
    }

    .section-head a,
    .blog-grid a,
    .listing-card {
      color: inherit;
      text-decoration: none;
    }

    .section-head a {
      color: var(--accent);
      font-weight: 900;
    }

    .listing-grid,
    .feed-grid,
    .blog-grid {
      display: grid;
      gap: 1rem;
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .listing-card,
    .feed-grid article,
    .blog-grid a {
      background: color-mix(in srgb, var(--paper) 72%, transparent);
      border: 1px solid var(--border);
      border-radius: 24px;
      overflow: hidden;
      padding: .8rem;
    }

    .listing-card app-responsive-image,
    .feed-media,
    .blog-grid app-responsive-image {
      aspect-ratio: 1 / 1;
      border-radius: 18px;
      display: grid;
      margin-bottom: .8rem;
      overflow: hidden;
      place-items: center;
    }

    .feed-media {
      background: #050505;
      position: relative;
    }

    .feed-media video {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }

    .live-chip {
      background: #ff3f4d;
      border-radius: 999px;
      color: white;
      font-size: .72rem;
      font-weight: 950;
      left: .65rem;
      padding: .35rem .55rem;
      position: absolute;
      top: .65rem;
      z-index: 1;
    }

    .listing-card h3,
    .feed-grid p,
    .blog-grid h3 {
      color: var(--ink);
      margin: .45rem 0;
    }

    .listing-card p,
    .blog-grid p {
      color: var(--muted);
      line-height: 1.5;
    }

    .listing-card strong {
      color: var(--accent);
    }

    .listing-category {
      color: var(--accent);
      display: block;
      font-size: .75rem;
      font-weight: 900;
      letter-spacing: .08em;
      margin-bottom: .35rem;
      text-transform: uppercase;
    }

    .fortune-products-section,
    .fortune-reviews-section {
      min-width: 0;
    }
    .fortune-products-section .listing-grid {
      grid-template-columns: 1fr;
    }
    .fortune-products-section .listing-card {
      align-items: center;
      display: grid;
      gap: .9rem;
      grid-template-columns: 76px minmax(0, 1fr);
      padding: .8rem;
    }
    .fortune-products-section .listing-card app-responsive-image {
      aspect-ratio: 1;
      border-radius: 16px;
      margin: 0;
      width: 76px;
    }
    .fortune-products-section .listing-card h3 { margin-top: 0; }
    .category-offer-section{grid-column:1/-1}
    .fortune-type-grid{display:grid;gap:24px 18px;grid-template-columns:repeat(5,minmax(0,1fr))}.fortune-type-card{align-items:center;color:var(--ink);display:flex;flex-direction:column;gap:8px;text-align:center;text-decoration:none}.fortune-type-image{background:var(--paper);border:6px solid #138c2e;border-radius:50%;display:grid;max-width:170px;overflow:hidden;place-items:center;width:100%;aspect-ratio:1}.fortune-type-image app-responsive-image{height:100%;width:100%}.fortune-type-image b{color:var(--accent);font-size:32px}.fortune-type-card>strong{font-size:16px}.fortune-type-card>small{color:var(--accent);font-weight:800}
    .review-list { display: grid; gap: .8rem; }
    .fortune-review-card {
      background: color-mix(in srgb, var(--paper) 72%, transparent);
      border: 1px solid var(--border);
      border-radius: 20px;
      display: grid;
      gap: .65rem;
      padding: 1rem;
    }
    .review-author { align-items: center; display: flex; gap: .7rem; }
    .review-author > div { display: grid; gap: .2rem; }
    .review-date { color: var(--muted); font-size: .75rem; margin-left: auto; white-space: nowrap; }
    .review-avatar {
      align-items: center;
      background: color-mix(in srgb, var(--accent) 16%, var(--paper));
      border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border));
      border-radius: 50%;
      color: var(--accent);
      display: inline-flex;
      flex: 0 0 38px;
      font-size: .8rem;
      font-weight: 950;
      height: 38px;
      justify-content: center;
      width: 38px;
    }
    .review-stars { display: inline-flex; gap: 1px; }
    .review-stars i { color: #c8cdd3; font-size: 1rem; font-style: normal; line-height: 1; }
    .review-stars i:not(.partial) { color: #f3b72f; }
    .review-stars i.partial { background: linear-gradient(90deg,#f3b72f var(--rating-fill),#c8cdd3 var(--rating-fill)); background-clip: text; -webkit-background-clip: text; color: transparent; }
    .fortune-review-card p { color: var(--muted); line-height: 1.55; margin: 0; }
    .review-product-link { text-decoration: none; }
    .legacy-reviews-section { display: none; }
    @media (max-width: 800px) { .fortune-type-grid{grid-template-columns:repeat(3,minmax(0,1fr))} }
    @media (max-width: 560px) { .review-date { align-self: start; font-size: .68rem; }.fortune-type-grid{gap:18px 10px;grid-template-columns:repeat(2,minmax(0,1fr))}.fortune-type-image{border-width:4px} }

    .seller-page:has(.fortune-products-section) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .seller-page:has(.fortune-products-section) .seller-hero,
    .seller-page:has(.fortune-products-section) .live-panel,
    .seller-page:has(.fortune-products-section) .feed-section,
    .seller-page:has(.fortune-products-section) .history-reviews-section,
    .seller-page:has(.fortune-products-section) .blogs-section {
      grid-column: 1 / -1;
    }

    .split-sections {
      display: grid;
      gap: 1rem;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    blockquote {
      border-left: 4px solid var(--accent);
      margin: 1rem 0 0;
      padding-left: 1rem;
    }
    .history-list{display:grid;gap:.7rem}.history-item{background:color-mix(in srgb,var(--accent) 6%,var(--card));border:1px solid var(--border);border-radius:16px;color:inherit;display:grid;gap:.2rem;padding:.8rem;text-decoration:none}.history-item:hover{border-color:var(--accent);transform:translateY(-1px)}.history-item strong{color:var(--ink)}.history-rating{color:#d18a00;font-weight:900}.history-comment{color:var(--muted);font-size:.92rem;margin:.25rem 0 0}.history-list .ghost{justify-self:start}
    .review-context {
      color: var(--accent);
      display: block;
      font-weight: 800;
      margin-top: .35rem;
    }
    .call-actions{align-items:center;display:flex;flex-wrap:wrap;gap:.65rem;margin-top:1rem}.call-actions button{background:var(--accent);border:0;border-radius:999px;color:var(--accent-contrast,#fff);font:inherit;font-weight:800;padding:.8rem 1rem}.call-actions button:disabled{cursor:not-allowed;filter:grayscale(1);opacity:.45}.call-actions small{opacity:.7}

    @keyframes livePulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.25); opacity: .68; }
    }

    @media (max-width: 900px) {
      .seller-hero,
      .split-sections {
        grid-template-columns: 1fr;
      }

      .seller-page:has(.fortune-products-section) { grid-template-columns: 1fr; }
      .seller-page:has(.fortune-products-section) .fortune-products-section,
      .seller-page:has(.fortune-products-section) .fortune-reviews-section { grid-column: 1; }

      .listing-grid,
      .feed-grid,
      .blog-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 520px) {
      .seller-page {
        gap: 1rem;
      }

      .seller-hero,
      .section-block,
      .live-panel {
        border-radius: 24px;
      }

      .section-head,
      .live-panel {
        align-items: flex-start;
        flex-direction: column;
      }

      .live-panel b {
        margin-left: 0;
      }
    }
  `]
})
export class SellerPage {
  private readonly pageData = inject(PageDataService);
  private readonly seo = inject(SeoService);
  private readonly route = inject(ActivatedRoute);
  private readonly site = inject(SiteConfigService);
  private readonly calls = inject(LiveCallService);
  private readonly api = inject(ApiClientService);
  protected readonly i18n = inject(I18nService);
  protected readonly commerce = computed(() => this.site.enabled('commerce') && !this.site.experience('fortune'));
  protected priceUnit(currency: string | null | undefined): string { const lang=this.i18n.lang(); return String(currency||'TRY').toUpperCase()==='CREDIT' ? ({tr:'Kredi',en:'credits',de:'Guthaben',es:'créditos',fr:'crédits'} as Record<string,string>)[lang] || 'credits' : String(currency||'TRY'); }
  protected listingAmount(listing: SellerListing): number {
    return this.creditPricingMode()
      ? Number(listing.minCredit ?? listing.credit ?? listing.minPrice ?? listing.price ?? 0)
      : Number(listing.minPrice ?? listing.price ?? 0);
  }
  protected listingUnit(listing: SellerListing): string {
    return this.creditPricingMode() ? this.priceUnit('CREDIT') : this.priceUnit(listing.currency || 'TRY');
  }
  protected creditPricingMode(): boolean {
    const config = this.site.config();
    return config.pricingMode === 'credit' || config.paymentMode === 'credit';
  }
  protected readonly copy = computed(() => {
    const copies = {
      tr: {
        shop: 'Mağaza', sales: 'satış', last7Days: 'son 7 gün', voiceCall: 'Sesli ara', videoCall: 'Görüntülü ara',
        creditsPerMinute: 'kredi/dk', busy: 'Şu anda meşgul', online: 'Çevrimiçi', inactive: 'Şu anda aktif değil',
        liveNow: 'CANLI YAYINDA', shopLive: 'Mağaza şu an canlı yayında', readerLive: 'Falcı şu an canlı yayında', viewers: 'izleyici',
        products: 'Ürünler', readings: 'Fallar', shopProducts: 'Mağazanın ürünleri', readerReadings: 'Falcının baktığı fallar',
        backProducts: 'Tüm ürünlere dön', backReadings: 'Fal seçimine dön', noProducts: 'Bu mağazanın aktif ürünü henüz görünmüyor.',
        noReadings: 'Bu falcının aktif falı henüz görünmüyor.', feed: 'Akış', shopPosts: 'Mağazanın paylaşımları',
        readerPosts: 'Falcının gönderdiği paylaşımlar', allFeed: 'Tüm akış', live: 'CANLI', shopPost: 'Mağaza paylaşımı',
        readerPost: 'Falcı paylaşımı', noShopPosts: 'Bu mağaza henüz paylaşım yapmamış.', noReaderPosts: 'Bu falcı henüz paylaşım yapmamış.',
        history: 'Geçmiş fallar', readingHistory: 'Geçmiş baktığı fallar', historyEmpty: 'Tamamlanan fal örnekleri burada listelenecek.', showMore: 'Daha fazla göster',
        reviews: 'Yorumlar', clientReviews: 'Danışan yorumları', client: 'Danışan', videoSession: 'Görüntülü görüşme',
        voiceSession: 'Sesli görüşme', audio: 'Ses', video: 'Görüntü', readerReply: 'Falcının cevabı',
        noReviews: 'Yorumlar henüz görünmüyor.', shopBlogs: 'Mağaza yazıları', readerBlogs: 'Falcı blogları',
        allBlogs: 'Tüm bloglar', shopLoading: 'Mağaza yükleniyor.', readerLoading: 'Falcı profili yükleniyor.', shopMissing: 'Mağaza bulunamadı', readerMissing: 'Falcı bulunamadı',
        shopSeoSuffix: 'mağazası', readerSeoSuffix: 'falcı profili',
        shopSeoDescription: 'ürünleri, yorumları ve mağaza paylaşımları.', readerSeoDescription: 'fal yorumları, aktif falları, paylaşımları ve canlı yayın durumu.', readingTypes:'FAL ÇEŞİTLERİ', offeredReadingTypes:'Baktığı fal çeşitleri', noReadingTypes:'Bu falcı henüz bir fal çeşidi açmadı.', readingReview:'Fal yorumu'
      },
      en: {
        shop: 'Shop', sales: 'sales', last7Days: 'last 7 days', voiceCall: 'Voice call', videoCall: 'Video call',
        creditsPerMinute: 'credits/min', busy: 'Busy now', online: 'Online', inactive: 'Currently offline',
        liveNow: 'LIVE NOW', shopLive: 'The shop is live now', readerLive: 'The reader is live now', viewers: 'viewers',
        products: 'Products', readings: 'Readings', shopProducts: 'Shop products', readerReadings: 'Readings offered',
        backProducts: 'Back to all products', backReadings: 'Back to readings', noProducts: 'No active products are visible yet.',
        noReadings: 'No active readings are visible yet.', feed: 'Feed', shopPosts: 'Shop posts',
        readerPosts: 'Reader posts', allFeed: 'View all posts', live: 'LIVE', shopPost: 'Shop post',
        readerPost: 'Reader post', noShopPosts: 'This shop has not posted yet.', noReaderPosts: 'This reader has not posted yet.',
        history: 'Past readings', readingHistory: 'Completed readings', historyEmpty: 'Completed reading examples will appear here.', showMore: 'Show more',
        reviews: 'Reviews', clientReviews: 'Client reviews', client: 'Client', videoSession: 'Video session',
        voiceSession: 'Voice session', audio: 'Audio', video: 'Video', readerReply: 'Reader reply',
        noReviews: 'No reviews are visible yet.', shopBlogs: 'Shop articles', readerBlogs: 'Reader articles',
        allBlogs: 'All articles', shopLoading: 'Loading shop.', readerLoading: 'Loading reader profile.', shopMissing: 'Shop not found', readerMissing: 'Reader not found',
        shopSeoSuffix: 'shop', readerSeoSuffix: 'reader profile',
        shopSeoDescription: 'products, reviews and shop posts.', readerSeoDescription: 'readings, reviews, posts and live availability.', readingTypes:'READING TYPES', offeredReadingTypes:'Readings offered', noReadingTypes:'This reader has not opened a reading type yet.', readingReview:'Reading review'
      },
      de: {
        shop: 'Shop', sales: 'Verkäufe', last7Days: 'letzte 7 Tage', voiceCall: 'Sprachanruf', videoCall: 'Videoanruf',
        creditsPerMinute: 'Credits/Min.', busy: 'Zurzeit beschäftigt', online: 'Online', inactive: 'Zurzeit nicht aktiv',
        liveNow: 'JETZT LIVE', shopLive: 'Der Shop ist jetzt live', readerLive: 'Der Berater ist jetzt live', viewers: 'Zuschauer',
        products: 'Produkte', readings: 'Deutungen', shopProducts: 'Produkte des Shops', readerReadings: 'Angebotene Deutungen',
        backProducts: 'Zu allen Produkten', backReadings: 'Zurück zu den Deutungen', noProducts: 'Noch keine aktiven Produkte sichtbar.',
        noReadings: 'Noch keine aktiven Deutungen sichtbar.', feed: 'Feed', shopPosts: 'Beiträge des Shops',
        readerPosts: 'Beiträge des Beraters', allFeed: 'Alle Beiträge', live: 'LIVE', shopPost: 'Shop-Beitrag',
        readerPost: 'Beraterbeitrag', noShopPosts: 'Dieser Shop hat noch nichts veröffentlicht.', noReaderPosts: 'Dieser Berater hat noch nichts veröffentlicht.',
        history: 'Frühere Deutungen', readingHistory: 'Abgeschlossene Deutungen', historyEmpty: 'Beispiele abgeschlossener Deutungen erscheinen hier.', showMore: 'Mehr anzeigen',
        reviews: 'Bewertungen', clientReviews: 'Kundenbewertungen', client: 'Kunde', videoSession: 'Videositzung',
        voiceSession: 'Sprachsitzung', audio: 'Ton', video: 'Bild', readerReply: 'Antwort des Beraters',
        noReviews: 'Noch keine Bewertungen sichtbar.', shopBlogs: 'Shop-Artikel', readerBlogs: 'Artikel des Beraters',
        allBlogs: 'Alle Artikel', shopLoading: 'Shop wird geladen.', readerLoading: 'Beraterprofil wird geladen.', shopMissing: 'Shop nicht gefunden', readerMissing: 'Berater nicht gefunden',
        shopSeoSuffix: 'Shop', readerSeoSuffix: 'Beraterprofil',
        shopSeoDescription: 'Produkte, Bewertungen und Beiträge.', readerSeoDescription: 'Deutungen, Bewertungen, Beiträge und Live-Verfügbarkeit.', readingTypes:'DEUTUNGSARTEN', offeredReadingTypes:'Angebotene Deutungen', noReadingTypes:'Dieser Berater bietet noch keine Deutungsart an.', readingReview:'Bewertung zur Deutung'
      },
      es: {
        shop: 'Tienda', sales: 'ventas', last7Days: 'últimos 7 días', voiceCall: 'Llamada de voz', videoCall: 'Videollamada',
        creditsPerMinute: 'créditos/min', busy: 'Ocupado ahora', online: 'En línea', inactive: 'No disponible ahora',
        liveNow: 'EN DIRECTO', shopLive: 'La tienda está en directo', readerLive: 'El vidente está en directo', viewers: 'espectadores',
        products: 'Productos', readings: 'Lecturas', shopProducts: 'Productos de la tienda', readerReadings: 'Lecturas disponibles',
        backProducts: 'Volver a productos', backReadings: 'Volver a lecturas', noProducts: 'Todavía no hay productos activos.',
        noReadings: 'Todavía no hay lecturas activas.', feed: 'Feed', shopPosts: 'Publicaciones de la tienda',
        readerPosts: 'Publicaciones del vidente', allFeed: 'Ver todo', live: 'EN DIRECTO', shopPost: 'Publicación de tienda',
        readerPost: 'Publicación del vidente', noShopPosts: 'Esta tienda aún no ha publicado.', noReaderPosts: 'Este vidente aún no ha publicado.',
        history: 'Lecturas anteriores', readingHistory: 'Lecturas completadas', historyEmpty: 'Aquí aparecerán ejemplos de lecturas completadas.', showMore: 'Mostrar más',
        reviews: 'Reseñas', clientReviews: 'Reseñas de clientes', client: 'Cliente', videoSession: 'Sesión de vídeo',
        voiceSession: 'Sesión de voz', audio: 'Audio', video: 'Vídeo', readerReply: 'Respuesta del vidente',
        noReviews: 'Todavía no hay reseñas.', shopBlogs: 'Artículos de la tienda', readerBlogs: 'Artículos del vidente',
        allBlogs: 'Todos los artículos', shopLoading: 'Cargando tienda.', readerLoading: 'Cargando perfil.', shopMissing: 'Tienda no encontrada', readerMissing: 'Vidente no encontrado',
        shopSeoSuffix: 'tienda', readerSeoSuffix: 'perfil de vidente',
        shopSeoDescription: 'productos, reseñas y publicaciones.', readerSeoDescription: 'lecturas, reseñas, publicaciones y disponibilidad.', readingTypes:'TIPOS DE LECTURA', offeredReadingTypes:'Lecturas disponibles', noReadingTypes:'Este vidente aún no ofrece ningún tipo de lectura.', readingReview:'Reseña de lectura'
      },
      fr: {
        shop: 'Boutique', sales: 'ventes', last7Days: '7 derniers jours', voiceCall: 'Appel audio', videoCall: 'Appel vidéo',
        creditsPerMinute: 'crédits/min', busy: 'Occupé actuellement', online: 'En ligne', inactive: 'Indisponible actuellement',
        liveNow: 'EN DIRECT', shopLive: 'La boutique est en direct', readerLive: 'Le voyant est en direct', viewers: 'spectateurs',
        products: 'Produits', readings: 'Voyances', shopProducts: 'Produits de la boutique', readerReadings: 'Voyances proposées',
        backProducts: 'Retour aux produits', backReadings: 'Retour aux voyances', noProducts: 'Aucun produit actif pour le moment.',
        noReadings: 'Aucune voyance active pour le moment.', feed: 'Fil', shopPosts: 'Publications de la boutique',
        readerPosts: 'Publications du voyant', allFeed: 'Tout voir', live: 'EN DIRECT', shopPost: 'Publication de boutique',
        readerPost: 'Publication du voyant', noShopPosts: 'Cette boutique n’a encore rien publié.', noReaderPosts: 'Ce voyant n’a encore rien publié.',
        history: 'Voyances précédentes', readingHistory: 'Voyances terminées', historyEmpty: 'Les exemples de voyances terminées apparaîtront ici.', showMore: 'Afficher plus',
        reviews: 'Avis', clientReviews: 'Avis des clients', client: 'Client', videoSession: 'Consultation vidéo',
        voiceSession: 'Consultation audio', audio: 'Son', video: 'Vidéo', readerReply: 'Réponse du voyant',
        noReviews: 'Aucun avis visible pour le moment.', shopBlogs: 'Articles de la boutique', readerBlogs: 'Articles du voyant',
        allBlogs: 'Tous les articles', shopLoading: 'Chargement de la boutique.', readerLoading: 'Chargement du profil.', shopMissing: 'Boutique introuvable', readerMissing: 'Voyant introuvable',
        shopSeoSuffix: 'boutique', readerSeoSuffix: 'profil de voyant',
        shopSeoDescription: 'produits, avis et publications.', readerSeoDescription: 'voyances, avis, publications et disponibilité.', readingTypes:'TYPES DE VOYANCE', offeredReadingTypes:'Voyances proposées', noReadingTypes:'Ce voyant ne propose pas encore de type de voyance.', readingReview:'Avis sur la voyance'
      }
    };
    return copies[this.i18n.lang()];
  });

  protected readonly raw = computed(() => this.pageData.get<SellerPageData>('sellerPage'));
  protected readonly missing = computed(() => {
    const response = this.raw();
    return response !== null && (response.success === false || response.result === false || response.data === null);
  });
  protected readonly payload = computed(() => this.raw()?.data ?? null);
  protected readonly seller = computed(() => this.payload()?.seller ?? null);
  protected readonly live = computed(() => this.payload()?.live ?? null);
  protected readonly listings = computed(() => {
    const items = this.payload()?.listings ?? this.payload()?.readings ?? [];
    return items.map((item) => ({
      ...item,
      title: item.title || (item as any).listingTitle || (item as any).name || '',
      slug: item.slug || (item as any).listingSlug || '',
      images: item.images?.length ? item.images : ((item as any).imageUrl ? [{ url: (item as any).imageUrl, sizes: (item as any).imageSizes }] : [])
      ,sellerImage: (item as any).sellerImage || (item as any).sellerMedia || null
      ,sellerImageSizes: (item as any).sellerImageSizes || (item as any).sellerMediaSizes || null
    }));
  });
  protected readonly fortuneOffers = computed(() => this.payload()?.fortuneOffers ?? []);
  protected readonly usesFortuneCategorySales = computed(() => this.site.config().experience?.salesModel === 'categoryOffer');
  protected readonly feeds = computed(() => this.payload()?.feeds ?? []);
  private readonly callReviews = signal<SellerReview[]>([]);
  protected readonly reviews = computed(() => {
    const seen = new Set<string>();
    return [...(this.payload()?.reviews ?? []), ...this.callReviews()]
      .filter((review) => {
        const id = Number(review.id || 0);
        const key = id > 0
          ? `id:${id}`
          : `fallback:${review.createdAt ?? ''}|${review.authorName ?? ''}|${review.rating}|${review.comment ?? ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => (Date.parse(String(b.createdAt ?? '')) || 0) - (Date.parse(String(a.createdAt ?? '')) || 0));
  });
  protected readonly blogs = computed(() => this.payload()?.blogs ?? []);
  protected readonly callAvailability = signal<CallAvailability | null>(null);

  constructor() {
    effect(() => {
      const seller = this.seller();
      const slug = this.route.snapshot.paramMap.get('slug') ?? '';
      if (!seller) {
        return;
      }
      this.seo.setPage({
        title: `${this.commerce() ? seller.shopName || seller.sellerName : seller.sellerName || seller.shopName} ${this.commerce() ? this.copy().shopSeoSuffix : this.copy().readerSeoSuffix}`,
        description: seller.description || `${this.commerce() ? seller.shopName || seller.sellerName : seller.sellerName || seller.shopName} ${this.commerce() ? this.copy().shopSeoDescription : this.copy().readerSeoDescription}`,
        image: seller.profilePicture ?? undefined,
        path: this.i18n.routePath('seller', { slug }),
        type: 'website'
      });
      if (!this.commerce()) {
        void this.loadCallAvailability(seller.sellerID || seller.id);
        void this.loadCallReviews(seller.sellerID || seller.id);
      }
    });
  }

  protected routeSlug(): string { return this.route.snapshot.paramMap.get('slug') ?? ''; }

  protected async startCall(type: 'voice'|'video'): Promise<void> {
    const sellerID=Number(this.seller()?.sellerID || this.seller()?.id || 0);
    if(sellerID) await this.calls.start(sellerID,type);
  }

  private async loadCallAvailability(sellerID:number):Promise<void>{
    this.callAvailability.set(await this.calls.availability(sellerID));
  }

  private async loadCallReviews(sellerID:number):Promise<void>{
    try {
      const response:any=await firstValueFrom(this.api.post('liveCallSellerReviews',{sellerID}));
      const data=response?.data?.data ?? response?.data ?? response ?? {};
      this.callReviews.set(Array.isArray(data.items) ? data.items : []);
    } catch { this.callReviews.set([]); }
  }

  protected initials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FD';
  }

  protected ratingStars(rating: number | null | undefined): Array<{ partial: boolean; fill: number }> {
    const value = Math.max(0, Math.min(5, Number(rating || 0)));
    return Array.from({ length: 5 }, (_, index) => {
      const fill = Math.max(0, Math.min(100, (value - index) * 100));
      return { partial: fill > 0 && fill < 100, fill };
    });
  }

  protected mediaUrl(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    if (/^https?:\/\//.test(url) || url.startsWith('/')) {
      return url;
    }
    return `/${url}`;
  }

  protected categoryLabel(title: string): string {
    return String(title || this.copy().readings).replace(/^Online\s+/i, '').split(/\s+[–—-]\s+/)[0].replace(/\s+Baktır$/i, '').trim();
  }
}
